import React, { useEffect, useState } from 'react';
import cls from "classnames";
import { isRemovableSupportLogin, isSupportLogin } from '../../../utils/user';
import styles from './EnvironmentIndicator.module.scss';
import FontAwesomeIcon from '../../core/fa_icon';
import { askRemoveSupportLock } from './support_login';
import { copyTextToClipboard } from '../../CopyableText/CopyableText';
import { TransitionedContainer } from '../../core/transitioned_container';

/**
 * Renders small indicators for support login and the current environment (alpha, dev).
 */
export const EnvironmentIndicators: React.FC = () => {
  const env = currentEnvironment();
  const _isRemovableSupportLogin = isRemovableSupportLogin();
  const _isSupportLogin = isSupportLogin;
  const [nonce, setNonce] = useState(0);
  const [hasCopiedCookies, setHasCopiedCookies] = useState(false);

  useEffect(() => {
    if (hasCopiedCookies) {
      const id = setTimeout(() => setHasCopiedCookies(false), 1500);
      return () => { clearTimeout(id); }
    }
  }, [hasCopiedCookies]);

  if (env === 'prod' && !_isRemovableSupportLogin && !_isSupportLogin) {
    return null;
  }

  return (
    <div className={styles.container}>
      {
        _isSupportLogin ? (
          <div
            className={cls([styles.indicator, styles.copyCookies])}
            title="Kopiera cookies"
            onClick={() => {
              copyTextToClipboard(getCopyCookieCode());
              setHasCopiedCookies(true);
            }}>
            <TransitionedContainer
              lock={hasCopiedCookies}
              style={{ transform: "rotate(-90deg)" }}
              transition={hasCopiedCookies ? "slide-down" : "slide-up"}
              transitionContainerStyle={{ width: 13, height: 18 }}>
              <FontAwesomeIcon
                name={hasCopiedCookies ? "check" : "cookie-bite"} />
            </TransitionedContainer>
          </div>
        ) : null
      }
      {
        _isRemovableSupportLogin ? (
          <div
            className={cls([styles.indicator, styles.support])}
            onClick={() => {
              if (askRemoveSupportLock()) {
                setNonce(Date.now());
              }
            }}>
            SALESYS&nbsp;&nbsp;KUNDSERVICE
            <FontAwesomeIcon name="lock" />
          </div>
        ) : null
      }
      {
        env !== "prod" ? (
          <div className={cls([styles.indicator, styles[env]])}>
            SALESYS&nbsp;&nbsp;{env.toUpperCase()}
          </div>
        ) : null
      }
    </div>
  )
}

/**
 * @returns Code for copying cookies.
 */
function getCopyCookieCode() {
  let code = document.cookie.split(';').map(cookie => cookie.trim()).filter(cookie => /^s2_/.test(cookie)).map(cookie => `document.cookie = '${cookie}; Path=/'`).join(';\n');
  code = code.trim();

  if (!code.endsWith(';')) {
    code += ';';
  }

  code += '\nif (document.location.pathname.startsWith("/login")) { document.location.href = "/"; }';
  return code;
}

export function currentEnvironment(): 'dev' | 'next' | 'alpha' | 'prod' {
  const { hostname, port } = document.location;
  if (hostname.includes('localhost') || hostname.endsWith('.local')) {
    return 'dev';
  }

  if (hostname.endsWith('elipt.is') && !port?.startsWith('3')) {
    return hostname.includes('salesys-next') ? 'next' : 'alpha';
  }

  //  s2-tel-detector med intern DNS.
  if (hostname === 's2-frontend') {
    return 'next';
  }

  if (hostname.endsWith('ngrok-free.app')) {
    return 'dev';
  }

  return 'prod';
}